import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { closeModal, showToast, setLotSize } from "../../store/uiSlice";
import {
  removePosition,
  updatePosition,
  setBalance,
  setPortfolio,
  addHistoryOrder,
} from "../../store/tradeSlice";
import { fmtP } from "../../utils/formatters";
import { LOT_LABELS, getLotSize } from "../../utils/constants";
import { getForexUSDPrice, getNotionalUSD } from "../../utils/forexUtils";
import api from "../../services/api";

const getSpread = (price, category) => {
  if (!price || price === 0) return 0;
  if (category === "forex") return 0.0002;
  if (category === "crypto") return price * 0.001;
  if (category === "metal") return price * 0.002;
  return price * 0.002;
};

const getLeverageMultiplier = (leverageStr, category) => {
  if (category === "crypto") return 25;
  if (category === "stock") return 4;
  if (!leverageStr) return 50;
  const parts = String(leverageStr).split(":");
  return parseInt(parts[1]) || parseInt(parts[0]) || 50;
};

const getLeverageDisplay = (category, userLeverage) => {
  if (category === "crypto") return "1:25";
  if (category === "stock") return "1:4";
  return userLeverage || "1:100";
};

const getMarginRate = (category, userLeverage) => {
  const multiplier = getLeverageMultiplier(userLeverage, category);
  return (100 / multiplier).toFixed(1);
};

const getLotLabel = (cat, id) => {
  if (id === "XAGUSD") return "1 lot = 1,000 oz";
  return LOT_LABELS[cat] || "1 lot = 1 unit";
};

const SellModal = ({ instrument }) => {
  const dispatch = useDispatch();
  const { sellModal } = useSelector((s) => s.ui.modals);
  const { lotSize } = useSelector((s) => s.ui);
  const { marketData, balance, portfolio } = useSelector((s) => s.trade);
  const user = useSelector((s) => s.auth.user);
  const theme = useSelector((s) => s.ui.theme);
  const isLight = theme === "light";

  const [orderType, setOrderType] = useState("market");
  const [quantity, setQuantity] = useState(lotSize || 0.01);
  const [qtyRaw, setQtyRaw] = useState(String(lotSize || 0.01));
  const [limitPrice, setLimitPrice] = useState("");
  const [takeProfit, setTakeProfit] = useState("");
  const [stopLoss, setStopLoss] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [estimation, setEstimation] = useState({
    execPrice: 0,
    totalValue: 0,
    requiredMargin: 0,
    pnl: 0,
    balanceAfter: 0,
    estimatedProfit: null,
    estimatedLoss: null,
    percentOfBalance: 0,
  });

  const position = instrument ? portfolio?.[instrument.id] : null;
  const hasLongPosition = position && Number(position.qty) > 0.001;
  const hasShortPosition = position && Number(position.qty) < -0.001;

  const userLeverage = user?.leverage || "1:100";
  const leverageMultiplier = getLeverageMultiplier(
    userLeverage,
    instrument?.cat,
  );
  const leverageDisplay = getLeverageDisplay(instrument?.cat, userLeverage);
  const marginRatePct = getMarginRate(instrument?.cat, userLeverage);

  // Theme-aware button styles
  const sellBtnBg = isLight
    ? "linear-gradient(to right, #dc2626, #b91c1c)"
    : "rgba(220, 38, 38, 0.2)";
  const sellBtnBorder = isLight ? "none" : "1px solid rgba(220, 38, 38, 0.35)";
  const sellBtnShadow = isLight ? "0 4px 16px rgba(220,38,38,0.3)" : "none";

  useEffect(() => {
    if (sellModal && instrument) {
      setOrderType("market");
      setQuantity(lotSize || 0.01);
      setQtyRaw(String(lotSize || 0.01));
      setLimitPrice("");
      setTakeProfit("");
      setStopLoss("");
    }
  }, [sellModal, instrument]);

  useEffect(() => {
    updateEstimation();
  }, [
    quantity,
    orderType,
    limitPrice,
    takeProfit,
    stopLoss,
    instrument,
    marketData,
    position,
    balance,
  ]);

  const updateEstimation = () => {
    if (!instrument) return;
    const data = marketData[instrument.id];
    const marketPrice = data?.price || 0;
    const spread = getSpread(marketPrice, instrument.cat);
    const ls = getLotSize(instrument.cat, instrument.id);
    const bidPrice = marketPrice - spread / 2;
    let execPrice =
      orderType === "market" ? bidPrice : parseFloat(limitPrice) || 0;
    if (orderType !== "market" && execPrice <= 0) {
      setEstimation({
        execPrice: 0,
        totalValue: 0,
        requiredMargin: 0,
        pnl: 0,
        balanceAfter: 0,
        estimatedProfit: null,
        estimatedLoss: null,
        percentOfBalance: 0,
      });
      return;
    }
    const totalValue = getNotionalUSD(
      instrument.id,
      execPrice,
      quantity,
      instrument.cat,
      ls,
      marketData,
    );
    const requiredMargin = totalValue / leverageMultiplier;
    const percentOfBalance = balance > 0 ? (requiredMargin / balance) * 100 : 0;
    const currentBalance = parseFloat(balance) || 0;
    let pnl = 0,
      balanceAfter = 0;
    if (hasLongPosition) {
      const posQty = Math.min(quantity, Number(position.qty));
      const avgEntry = Number(position.avgPrice) || 0;
      const usdEntry = getForexUSDPrice(
        instrument.id,
        avgEntry,
        instrument.cat,
        marketData,
      );
      const usdExit = getForexUSDPrice(
        instrument.id,
        execPrice,
        instrument.cat,
        marketData,
      );
      pnl = (usdExit - usdEntry) * posQty * ls;
      const marginToReturn =
        Number(position.cost) * (posQty / Number(position.qty));
      balanceAfter = currentBalance + marginToReturn + pnl;
    } else {
      balanceAfter = currentBalance - requiredMargin;
    }

    // ✅ Estimated Profit / Loss for OPENING a new SHORT position
    // For a short: profit when price drops (TP < entry), loss when price rises (SL > entry)
    let estimatedProfit = null,
      estimatedLoss = null;

    if (!hasLongPosition && !hasShortPosition) {
      const tp = parseFloat(takeProfit);
      if (takeProfit && !isNaN(tp) && tp > 0 && tp < execPrice) {
        const usdEntry = getForexUSDPrice(
          instrument.id,
          execPrice,
          instrument.cat,
          marketData,
        );
        const usdTp = getForexUSDPrice(
          instrument.id,
          tp,
          instrument.cat,
          marketData,
        );
        // Short profits when price falls: entry - exit (positive)
        estimatedProfit = (usdEntry - usdTp) * quantity * ls;
      }

      const sl = parseFloat(stopLoss);
      if (stopLoss && !isNaN(sl) && sl > execPrice) {
        const usdEntry = getForexUSDPrice(
          instrument.id,
          execPrice,
          instrument.cat,
          marketData,
        );
        const usdSl = getForexUSDPrice(
          instrument.id,
          sl,
          instrument.cat,
          marketData,
        );
        // Short loses when price rises: entry - exit (negative)
        estimatedLoss = (usdEntry - usdSl) * quantity * ls;
      }
    }

    setEstimation({
      execPrice,
      totalValue,
      requiredMargin,
      pnl,
      balanceAfter,
      estimatedProfit,
      estimatedLoss,
      percentOfBalance,
    });
  };

  const applyQty = (raw) => {
    setQtyRaw(raw);
    let v = parseFloat(raw);
    if (isNaN(v) || v < 0.01) return;
    if (v > 100) v = 100;
    v = parseFloat(v.toFixed(2));
    setQuantity(v);
    dispatch(setLotSize(v));
  };
  const blurQty = () => {
    let v = parseFloat(qtyRaw);
    if (isNaN(v) || v < 0.01) v = 0.01;
    if (v > 100) v = 100;
    v = parseFloat(v.toFixed(2));
    setQuantity(v);
    setQtyRaw(String(v));
    dispatch(setLotSize(v));
  };
  const stepQty = (delta) => {
    let v = quantity + delta;
    if (v < 0.01) v = 0.01;
    if (v > 100) v = 100;
    v = parseFloat(v.toFixed(2));
    setQuantity(v);
    setQtyRaw(String(v));
    dispatch(setLotSize(v));
  };

  const refreshUserData = async () => {
    try {
      const [balRes, portRes] = await Promise.all([
        api.get("/user/balance"),
        api.get("/user/portfolio"),
      ]);
      dispatch(setBalance(parseFloat(balRes.data.balance) || 0));
      dispatch(setPortfolio(portRes.data));
    } catch (e) {}
  };

  const handleSubmit = async () => {
    if (!instrument || isSubmitting) return;
    if (!user?.questionnaireComplete) {
      dispatch(
        showToast({
          message:
            "Please complete the trading questionnaire before placing trades",
          type: "error",
          duration: 5000,
        }),
      );
      window.dispatchEvent(new CustomEvent("open-questionnaire"));
      return;
    }
    const data = marketData[instrument.id];
    if (!data?.price) {
      dispatch(showToast({ message: "Price not available", type: "error" }));
      return;
    }
    if (hasShortPosition) {
      dispatch(
        showToast({
          message: "You have a SHORT position. Use BUY to close it.",
          type: "error",
        }),
      );
      return;
    }
    if (
      !hasLongPosition &&
      estimation.requiredMargin > parseFloat(balance || 0)
    ) {
      dispatch(
        showToast({
          message: `Insufficient margin. Need $${estimation.requiredMargin.toFixed(2)} (${leverageDisplay})`,
          type: "error",
        }),
      );
      return;
    }
    const spread = getSpread(data.price, instrument.cat);
    let execPrice = data.price - spread / 2;
    if (orderType !== "market") execPrice = parseFloat(limitPrice);
    setIsSubmitting(true);
    const payload = {
      instrumentId: instrument.id,
      category: instrument.cat,
      quantity,
      orderType,
      price: execPrice,
    };
    // ✅ Include TP/SL only when opening a new short (not closing a long)
    if (!hasLongPosition && !hasShortPosition) {
      const tp = parseFloat(takeProfit);
      const sl = parseFloat(stopLoss);
      if (takeProfit && !isNaN(tp) && tp > 0) payload.takeProfit = tp;
      if (stopLoss && !isNaN(sl) && sl > 0) payload.stopLoss = sl;
    }
    try {
      const response = await api.post("/trade/sell", payload);
      if (response.data.success) {
        if (response.data.newBalance !== undefined)
          dispatch(setBalance(parseFloat(response.data.newBalance)));
        const returnedPosition = response.data.position;
        const remainingQty = response.data.remainingQty ?? 0;
        if (!returnedPosition || Math.abs(remainingQty) <= 0.001)
          dispatch(removePosition(instrument.id));
        else
          dispatch(
            updatePosition({
              instrumentId: instrument.id,
              position: returnedPosition,
            }),
          );
        if (response.data.trade && response.data.trade.status === "closed")
          dispatch(addHistoryOrder(response.data.trade));
        dispatch(
          showToast({
            message: `SELL ${quantity} lot(s) ${instrument.id}`,
            type: "sell",
          }),
        );
        dispatch(closeModal("sellModal"));
        await refreshUserData();
      }
    } catch (error) {
      dispatch(
        showToast({
          message:
            error.response?.data?.message || "Failed to execute sell order",
          type: "error",
        }),
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!instrument) return null;
  const data = marketData[instrument.id];
  const marketPrice = data?.price || 0;
  const spread = getSpread(marketPrice, instrument.cat);
  const bidPrice = marketPrice - spread / 2;
  const riskLevel = estimation.percentOfBalance;
  const getRiskColor = () => {
    if (riskLevel < 5) return "var(--color-green)";
    if (riskLevel < 15) return "var(--color-yellow)";
    if (riskLevel < 30) return "var(--color-text)";
    return "var(--color-red)";
  };
  const getRiskLabel = () => {
    if (riskLevel < 5) return "Low";
    if (riskLevel < 15) return "Medium";
    if (riskLevel < 30) return "High";
    return "Critical";
  };
  const fmtEst = (val) => {
    if (val === null || val === undefined) return "—";
    const abs = Math.abs(val);
    const sign = val >= 0 ? "+" : "-";
    return `${sign}$${abs.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const inputStyle = {
    background: "var(--color-bgInput)",
    border: "1px solid var(--color-borderMedium)",
    color: "var(--color-text)",
  };

  return (
    <>
      <style>{`
        .custom-scrollbar::-webkit-scrollbar { width: 14px; height: 14px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: var(--color-bgSurfaceLight); border-radius: 12px; margin: 8px 2px; border: 1px solid var(--color-borderMedium); }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: linear-gradient(180deg, var(--color-orange), var(--color-orangeDark)); border-radius: 12px; border: 3px solid var(--color-bgSurfaceLight); min-height: 50px; }
        .custom-scrollbar { scrollbar-width: auto; scrollbar-color: var(--color-orange) var(--color-bgSurfaceLight); }
      `}</style>

      <div
        className={`fixed inset-0 z-[800] flex items-center justify-center p-0 sm:p-4 transition-all duration-300 ${sellModal ? "visible" : "invisible"}`}
      >
        <div
          className={`absolute inset-0 backdrop-blur-md transition-opacity duration-300 ${sellModal ? "opacity-100" : "opacity-0"}`}
          style={{ background: "var(--color-bgOverlay)" }}
          onClick={() => dispatch(closeModal("sellModal"))}
        />
        <div
          className={`relative sm:rounded-2xl w-full max-w-[520px] h-full sm:max-h-[90vh] overflow-y-auto shadow-2xl transition-all duration-300 custom-scrollbar ${sellModal ? "opacity-100 scale-100" : "opacity-0 scale-95"}`}
          style={{
            background: "var(--color-bgSurface)",
            border: "1px solid var(--color-border)",
          }}
        >
          {/* Header */}
          <div
            className="sticky top-0 z-10 px-4 sm:px-5 py-3 sm:py-4 flex items-center justify-between"
            style={{
              background: "var(--color-bgSurface)",
              borderBottom: "1px solid var(--color-borderMedium)",
            }}
          >
            <div className="flex items-center gap-2 sm:gap-3 min-w-0">
              <div
                className="w-8 h-8 sm:w-9 sm:h-9 rounded-xl flex items-center justify-center text-sm sm:text-base flex-shrink-0"
                style={{
                  background: "var(--color-redBg)",
                  border: "1px solid var(--color-redBorder)",
                  color: "var(--color-red)",
                }}
              >
                <i className="fas fa-arrow-trend-down" />
              </div>
              <div className="min-w-0">
                <div
                  className="font-syne text-base sm:text-lg font-extrabold truncate"
                  style={{ color: "var(--color-text)" }}
                >
                  {hasLongPosition ? "SELL" : "SELL / SHORT"} — {instrument.id}
                </div>
                <div
                  className="text-[11px] sm:text-xs font-mono truncate"
                  style={{ color: "var(--color-textSecondary)" }}
                >
                  {hasLongPosition
                    ? "Closing long position"
                    : orderType === "market"
                      ? "Market Order"
                      : orderType === "limit"
                        ? "Limit Order"
                        : "Stop Order"}{" "}
                  · {leverageDisplay} · {marginRatePct}% margin
                </div>
              </div>
            </div>
            <button
              onClick={() => dispatch(closeModal("sellModal"))}
              className="w-8 h-8 sm:w-7 sm:h-7 rounded-md cursor-pointer flex items-center justify-center transition-all active:scale-90 flex-shrink-0"
              style={{
                background: "var(--color-bgCard)",
                border: "1px solid var(--color-borderMedium)",
                color: "var(--color-textSecondary)",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = "var(--color-red)";
                e.currentTarget.style.color = "var(--color-red)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = "var(--color-borderMedium)";
                e.currentTarget.style.color = "var(--color-textSecondary)";
              }}
            >
              <i className="fas fa-xmark text-sm" />
            </button>
          </div>

          {/* Stats Row */}
          <div
            className="grid grid-cols-3 gap-1.5 sm:gap-2 p-3 sm:p-4"
            style={{ borderBottom: "1px solid var(--color-borderMedium)" }}
          >
            {[
              {
                label: "Bid",
                value: fmtP(bidPrice, instrument.cat),
                color: "var(--color-red)",
                sub: "Sell price",
              },
              {
                label: "Balance",
                value: `$${parseFloat(balance || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
                color: "var(--color-text)",
                sub: "Available",
              },
              {
                label: "Leverage",
                value: leverageDisplay,
                color: "var(--color-purpleLight)",
                sub: `${marginRatePct}% margin`,
              },
            ].map((item) => (
              <div
                key={item.label}
                className="rounded-xl p-2 text-center"
                style={{
                  background: "var(--color-bgCard)",
                  border: "1px solid var(--color-borderMedium)",
                }}
              >
                <div
                  className="text-[9px] sm:text-[10px] font-mono tracking-[0.1em] uppercase mb-1"
                  style={{ color: "var(--color-textSecondary)" }}
                >
                  {item.label}
                </div>
                <div
                  className="font-syne text-sm sm:text-lg font-extrabold"
                  style={{ color: item.color }}
                >
                  {item.value}
                </div>
                <div
                  className="text-[8px] sm:text-[9px] font-mono mt-0.5 hidden sm:block"
                  style={{ color: "var(--color-textSecondary)" }}
                >
                  {item.sub}
                </div>
              </div>
            ))}
          </div>

          {/* Has Short Position Warning */}
          {hasShortPosition && (
            <div
              className="mx-4 sm:mx-5 mt-3 sm:mt-4 p-3 rounded-xl flex gap-2.5"
              style={{
                background: "var(--color-redBg)",
                border: "1px solid var(--color-redBorder)",
              }}
            >
              <i
                className="fas fa-triangle-exclamation text-sm mt-0.5 flex-shrink-0"
                style={{ color: "var(--color-red)" }}
              />
              <div
                className="text-[11px] font-mono leading-relaxed"
                style={{ color: "var(--color-redLight)" }}
              >
                You have a <b>SHORT</b> position on {instrument.id}.<br />
                Use <b>BUY</b> to close it.
              </div>
            </div>
          )}

          {/* No Position - Opening Short Info */}
          {!hasLongPosition && !hasShortPosition && (
            <div
              className="mx-4 sm:mx-5 mt-3 sm:mt-4 p-3 rounded-xl flex gap-2.5"
              style={{
                background: "var(--color-yellowBg)",
                border: "1px solid var(--color-yellowBorder)",
              }}
            >
              <i
                className="fas fa-info-circle text-sm mt-0.5 flex-shrink-0"
                style={{ color: "var(--color-yellow)" }}
              />
              <div
                className="text-[11px] font-mono leading-relaxed"
                style={{ color: "var(--color-yellow)" }}
              >
                <b>SHORT selling:</b> Only margin is blocked —{" "}
                <b>${estimation.requiredMargin.toFixed(2)}</b> at{" "}
                {leverageDisplay}
              </div>
            </div>
          )}

          {/* Has Long Position - Closing Info */}
          {hasLongPosition && (
            <div
              className="mx-4 sm:mx-5 mt-3 sm:mt-4 p-3 rounded-xl flex gap-2.5"
              style={{
                background: "var(--color-greenBg)",
                border: "1px solid var(--color-greenBorder)",
              }}
            >
              <i
                className="fas fa-info-circle text-sm mt-0.5 flex-shrink-0"
                style={{ color: "var(--color-green)" }}
              />
              <div
                className="text-[11px] font-mono leading-relaxed"
                style={{ color: "var(--color-greenLight)" }}
              >
                You have a <b>LONG</b> position on {instrument.id}.<br />
                Selling will close it and realise your P&amp;L.
              </div>
            </div>
          )}

          {/* Order Type */}
          <div
            className="px-4 sm:px-5 pt-3 sm:pt-4 pb-2"
            style={{ borderBottom: "1px solid var(--color-borderMedium)" }}
          >
            <div
              className="text-[10px] font-mono tracking-[0.1em] uppercase mb-2 flex items-center gap-1"
              style={{ color: "var(--color-textSecondary)" }}
            >
              <i
                className="fas fa-list-check text-[10px]"
                style={{ color: "var(--color-text)" }}
              />{" "}
              Order Type
            </div>
            <div className="grid grid-cols-3 gap-1 sm:gap-1.5">
              {[
                {
                  type: "market",
                  label: "MARKET",
                  icon: "fa-bolt",
                  desc: "Execute now",
                },
                {
                  type: "limit",
                  label: "SELL LIMIT",
                  icon: "fa-arrow-trend-up",
                  desc: "Sell when rises",
                },
                {
                  type: "stop",
                  label: "SELL STOP",
                  icon: "fa-arrow-trend-down",
                  desc: "Sell on breakdown",
                },
              ].map((option) => (
                <button
                  key={option.type}
                  onClick={() => setOrderType(option.type)}
                  className="py-2 sm:py-2 rounded-lg text-center transition-all border active:scale-95"
                  style={{
                    background:
                      orderType === option.type
                        ? "var(--color-redBg)"
                        : "var(--color-bgCard)",
                    borderColor:
                      orderType === option.type
                        ? "var(--color-redBorder)"
                        : "var(--color-borderMedium)",
                    color:
                      orderType === option.type
                        ? "var(--color-red)"
                        : "var(--color-textSecondary)",
                    minHeight: "48px",
                  }}
                >
                  <div className="text-base mb-0.5">
                    <i className={`fas ${option.icon}`} />
                  </div>
                  <div className="text-[10px] font-syne font-bold">
                    {option.label}
                  </div>
                  <div
                    className="text-[8px] mt-0.5 hidden sm:block"
                    style={{ color: "var(--color-textSecondary)" }}
                  >
                    {option.desc}
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Order Parameters */}
          <div
            className="px-4 sm:px-5 py-3 sm:py-4"
            style={{ borderBottom: "1px solid var(--color-borderMedium)" }}
          >
            <div
              className="text-[10px] font-mono tracking-[0.1em] uppercase mb-3 flex items-center gap-1"
              style={{ color: "var(--color-textSecondary)" }}
            >
              <i
                className="fas fa-sliders text-[10px]"
                style={{ color: "var(--color-text)" }}
              />{" "}
              Order Parameters
            </div>
            <div className="grid grid-cols-2 gap-2 sm:gap-3 mb-3">
              <div>
                <div
                  className="text-[10px] font-mono mb-2 flex items-center gap-1"
                  style={{ color: "var(--color-textSecondary)" }}
                >
                  <i
                    className="fas fa-layer-group text-[9px]"
                    style={{ color: "var(--color-text)" }}
                  />{" "}
                  Quantity (Lots)
                </div>
                <div
                  className="flex items-center justify-center rounded-lg overflow-hidden"
                  style={{
                    border: "1px solid var(--color-borderMedium)",
                    background: "var(--color-bgInput)",
                  }}
                >
                  <button
                    onClick={() => stepQty(-0.01)}
                    className="w-10 h-10 sm:w-9 sm:h-9 cursor-pointer flex items-center justify-center active:scale-90 transition-all font-bold text-xl"
                    style={{
                      background: "var(--color-bgSurfaceLight)",
                      borderRight: "1px solid var(--color-borderMedium)",
                      color: "var(--color-text)",
                    }}
                  >
                    −
                  </button>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={qtyRaw}
                    onChange={(e) => applyQty(e.target.value)}
                    onBlur={blurQty}
                    className="flex-1 bg-transparent border-none text-center font-mono text-lg sm:text-xl font-bold py-2.5 outline-none"
                    style={{ color: "var(--color-text)" }}
                  />
                  <button
                    onClick={() => stepQty(0.01)}
                    className="w-10 h-10 sm:w-9 sm:h-9 cursor-pointer flex items-center justify-center active:scale-90 transition-all font-bold text-xl"
                    style={{
                      background: "var(--color-bgSurfaceLight)",
                      borderLeft: "1px solid var(--color-borderMedium)",
                      color: "var(--color-text)",
                    }}
                  >
                    +
                  </button>
                </div>
                <div
                  className="text-[9px] font-mono mt-2 text-center"
                  style={{ color: "var(--color-textSecondary)" }}
                >
                  {getLotLabel(instrument.cat, instrument.id)}
                </div>
              </div>
              {orderType !== "market" && (
                <div>
                  <div
                    className="text-[10px] font-mono mb-2 flex items-center gap-1"
                    style={{ color: "var(--color-textSecondary)" }}
                  >
                    <i
                      className="fas fa-tag text-[9px]"
                      style={{ color: "var(--color-text)" }}
                    />{" "}
                    Target Price
                  </div>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={limitPrice}
                    onChange={(e) => setLimitPrice(e.target.value)}
                    placeholder={
                      orderType === "limit" ? "Above market" : "Below market"
                    }
                    className="w-full rounded-lg px-3 py-2.5 sm:py-2 font-mono text-base outline-none transition-all"
                    style={inputStyle}
                    onFocus={(e) =>
                      (e.target.style.borderColor = "var(--color-orange)")
                    }
                    onBlur={(e) =>
                      (e.target.style.borderColor = "var(--color-borderMedium)")
                    }
                  />
                  <div
                    className="text-[9px] font-mono mt-1"
                    style={{ color: "var(--color-textSecondary)" }}
                  >
                    Bid: {fmtP(bidPrice, instrument.cat)}
                  </div>
                </div>
              )}
            </div>
            <div className="grid grid-cols-2 gap-2 sm:gap-3">
              <div>
                <div
                  className="text-[10px] font-mono mb-1"
                  style={{ color: "var(--color-green)" }}
                >
                  <i className="fas fa-bullseye text-[9px] mr-1" /> Take Profit
                </div>
                <input
                  type="text"
                  inputMode="decimal"
                  value={takeProfit}
                  onChange={(e) => setTakeProfit(e.target.value)}
                  placeholder={
                    hasLongPosition
                      ? `> ${fmtP(bidPrice, instrument.cat)}`
                      : `< ${fmtP(bidPrice, instrument.cat)}`
                  }
                  className="w-full rounded-lg px-3 py-2.5 sm:py-2 font-mono text-base outline-none transition-all"
                  style={inputStyle}
                  onFocus={(e) =>
                    (e.target.style.borderColor = "var(--color-green)")
                  }
                  onBlur={(e) =>
                    (e.target.style.borderColor = "var(--color-borderMedium)")
                  }
                />
              </div>
              <div>
                <div
                  className="text-[10px] font-mono mb-1"
                  style={{ color: "var(--color-red)" }}
                >
                  <i className="fas fa-shield-halved text-[9px] mr-1" /> Stop
                  Loss
                </div>
                <input
                  type="text"
                  inputMode="decimal"
                  value={stopLoss}
                  onChange={(e) => setStopLoss(e.target.value)}
                  placeholder={
                    hasLongPosition
                      ? `< ${fmtP(bidPrice, instrument.cat)}`
                      : `> ${fmtP(bidPrice, instrument.cat)}`
                  }
                  className="w-full rounded-lg px-3 py-2.5 sm:py-2 font-mono text-base outline-none transition-all"
                  style={inputStyle}
                  onFocus={(e) =>
                    (e.target.style.borderColor = "var(--color-red)")
                  }
                  onBlur={(e) =>
                    (e.target.style.borderColor = "var(--color-borderMedium)")
                  }
                />
              </div>
            </div>
          </div>

          {/* Order Estimation */}
          <div
            className="mx-4 sm:mx-5 my-3 sm:my-4 rounded-xl overflow-hidden"
            style={{ border: "1px solid var(--color-borderMedium)" }}
          >
            <div
              className="flex items-center justify-between px-3 py-2 border-b"
              style={{
                background: "var(--color-bgCard)",
                borderColor: "var(--color-borderMedium)",
              }}
            >
              <span
                className="text-[11px] font-syne font-bold flex items-center gap-1.5"
                style={{ color: "var(--color-text)" }}
              >
                <i className="fas fa-calculator text-[10px]" /> Order Estimation
              </span>
              <span
                className="text-[9px] px-1.5 py-0.5 rounded font-mono font-bold"
                style={{
                  background: "var(--color-redBg)",
                  color: "var(--color-red)",
                  border: "1px solid var(--color-redBorder)",
                }}
              >
                {orderType.toUpperCase()}
              </span>
            </div>
            <div
              className="divide-y"
              style={{
                background: "var(--color-bgCard)",
                borderColor: "var(--color-borderLight)",
              }}
            >
              {[
                {
                  label: "Total Position Value",
                  value: `$${estimation.totalValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
                  color: "var(--color-textSecondary)",
                },
                ...(hasLongPosition
                  ? [
                      {
                        label: "Estimated P&L",
                        value: `${estimation.pnl >= 0 ? "+" : "-"}$${Math.abs(estimation.pnl).toFixed(2)}`,
                        color:
                          estimation.pnl >= 0
                            ? "var(--color-green)"
                            : "var(--color-red)",
                      },
                    ]
                  : [
                      {
                        label: `Required Margin (${leverageDisplay})`,
                        value: `$${estimation.requiredMargin.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
                        color: "var(--color-text)",
                        highlight: true,
                      },
                    ]),
                // ✅ Estimated Profit (TP Hit) — only when opening a new short
                ...(estimation.estimatedProfit !== null
                  ? [
                      {
                        label: "Est. Profit (TP Hit)",
                        value: fmtEst(estimation.estimatedProfit),
                        color: "var(--color-green)",
                        highlight: true,
                      },
                    ]
                  : []),
                // ✅ Estimated Loss (SL Hit) — only when opening a new short
                ...(estimation.estimatedLoss !== null
                  ? [
                      {
                        label: "Est. Loss (SL Hit)",
                        value: fmtEst(estimation.estimatedLoss),
                        color: "var(--color-red)",
                        highlight: true,
                      },
                    ]
                  : []),
                {
                  label: "% of Balance",
                  value: `${estimation.percentOfBalance.toFixed(2)}%`,
                  color: getRiskColor(),
                },
              ].map((row, i) => (
                <div
                  key={i}
                  className="flex justify-between items-center px-3 py-2"
                  style={{
                    background: row.highlight
                      ? "var(--color-orangeBg)"
                      : "transparent",
                  }}
                >
                  <span
                    className="text-[10px] sm:text-[11px] font-bold"
                    style={{ color: row.color }}
                  >
                    {row.label}
                  </span>
                  <span
                    className="font-mono text-[11px] sm:text-xs font-bold"
                    style={{ color: row.color }}
                  >
                    {row.value}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Risk Bar */}
          {!hasLongPosition && !hasShortPosition && (
            <div className="px-4 sm:px-5 pb-3 sm:pb-4">
              <div className="flex justify-between items-center mb-1">
                <span
                  className="text-[9px] font-mono"
                  style={{ color: "var(--color-textSecondary)" }}
                >
                  Position Risk
                </span>
                <span
                  className="text-[9px] font-mono font-bold"
                  style={{ color: getRiskColor() }}
                >
                  {getRiskLabel()}
                </span>
              </div>
              <div
                className="h-1.5 rounded-full overflow-hidden"
                style={{ background: "var(--color-borderMedium)" }}
              >
                <div
                  className="h-full rounded-full transition-all duration-300"
                  style={{
                    width: `${Math.min(riskLevel * 2, 100)}%`,
                    background: getRiskColor(),
                  }}
                />
              </div>
            </div>
          )}

          {/* Insufficient Margin */}
          {!hasLongPosition &&
            !hasShortPosition &&
            estimation.requiredMargin > parseFloat(balance || 0) && (
              <div
                className="mx-4 sm:mx-5 mb-3 p-3 rounded-xl flex gap-2.5"
                style={{
                  background: "var(--color-redBg)",
                  border: "1px solid var(--color-redBorder)",
                }}
              >
                <i
                  className="fas fa-triangle-exclamation text-sm mt-0.5 flex-shrink-0"
                  style={{ color: "var(--color-red)" }}
                />
                <div
                  className="text-[11px] font-mono leading-relaxed"
                  style={{ color: "var(--color-redLight)" }}
                >
                  Insufficient margin.
                </div>
              </div>
            )}

          {/* Action Buttons */}
          <div
            className="flex gap-2 p-4 sm:p-5 pt-2"
            style={{
              borderTop: "1px solid var(--color-borderMedium)",
              background: "var(--color-bgCard)",
            }}
          >
            <button
              onClick={() => dispatch(closeModal("sellModal"))}
              className="flex-1 py-3.5 sm:py-3 rounded-full font-syne font-bold text-sm transition-all active:scale-95"
              style={{
                background: "var(--color-bgCard)",
                border: "1px solid var(--color-borderHover)",
                color: "var(--color-textSecondary)",
                minHeight: "48px",
              }}
            >
              <i className="fas fa-xmark mr-1" /> Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={
                isSubmitting ||
                hasShortPosition ||
                (!hasLongPosition &&
                  estimation.requiredMargin > parseFloat(balance || 0))
              }
              className="flex-1 py-3.5 sm:py-3 rounded-full font-syne font-bold text-sm transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
              style={{
                background: sellBtnBg,
                border: sellBtnBorder,
                color: "#ffffff",
                minHeight: "48px",
                boxShadow: sellBtnShadow,
              }}
            >
              {isSubmitting ? (
                <>
                  <span className="inline-block w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin mr-1" />
                  Processing…
                </>
              ) : (
                <>
                  <i className="fas fa-arrow-trend-down mr-1" />
                  Confirm SELL
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default SellModal;